import { supplyService, type SupplyTableProduct, type SupplyTableResponse } from '@/services/supplyService';
import type { ApiError } from '@/services/api';

/** Debe ser <= SUPPLY_TABLE_CHUNK_MAX_PAGE_SIZE del backend. */
export const SUPPLY_TABLE_CHUNK_SIZE = 2000;

/** Idle antes de arrancar el catálogo completo tras pintar qty>0 (SUP-CACHE-001). */
export const SUPPLY_CATALOG_IDLE_TIMEOUT_MS = 1500;

export function buildMergedSupplyResponse(
  first: SupplyTableResponse,
  rows: SupplyTableProduct[],
  totalCount: number,
  totalPages: number,
  pageSize: number,
): SupplyTableResponse {
  return {
    ...first,
    data: rows,
    count: rows.length,
    pagination: first.pagination
      ? {
          ...first.pagination,
          total_count: totalCount,
          total_pages: totalPages,
          current_page: 1,
          page_size: pageSize,
          has_next: totalPages > 1,
          has_previous: false,
          next_page: totalPages > 1 ? 2 : null,
          previous_page: null,
        }
      : first.pagination,
  };
}

function isCacheMissError(error: unknown): boolean {
  return Boolean(
    error
    && typeof error === 'object'
    && 'status' in error
    && (error as ApiError).status === 409,
  );
}

function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/** Espera idle corto (o timeout) antes del dump/bulk del catálogo completo. */
export function waitForSupplyCatalogIdle(
  timeoutMs: number = SUPPLY_CATALOG_IDLE_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason instanceof DOMException
        ? signal.reason
        : new DOMException('Aborted', 'AbortError'));
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      resolve();
    };
    const onAbort = () => {
      if (settled) return;
      settled = true;
      reject(signal?.reason instanceof DOMException
        ? signal.reason
        : new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    const ric = typeof window !== 'undefined'
      ? (window as Window & {
        requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number;
      }).requestIdleCallback
      : undefined;

    if (typeof ric === 'function') {
      ric(() => finish(), { timeout: timeoutMs });
    } else {
      setTimeout(finish, timeoutMs);
    }
  });
}

async function tryCatalogDump(
  apiFilters: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<SupplyTableResponse | null> {
  try {
    return await supplyService.getSupplyTableData(
      {
        ...apiFilters,
        catalog_dump: true,
      },
      { signal },
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    if (isCacheMissError(error)) {
      return null;
    }
    throw error;
  }
}

/**
 * Descarga el dataset de `supply-table` para un scope de filtros.
 *
 * Orden (SUP-CACHE-001):
 * 1. `catalog_dump=1` (HIT → fin; MISS → 409)
 * 2. page=1 (build / calienta Redis; `force_refresh` solo aquí si aplica)
 * 3. reintento dump; si sigue MISS → páginas 2..N con yield entre requests
 */
export async function fetchAllSupplyTablePages(
  apiFilters: Record<string, unknown>,
  options: {
    pageSize?: number;
    forceRefreshFirstPage?: boolean;
    signal?: AbortSignal;
    isStale?: () => boolean;
    onFirstPage?: (partial: SupplyTableResponse, totalPages: number) => void;
    /** Si true, omite dump y usa solo paginado (p.ej. alcance producción). */
    skipCatalogDump?: boolean;
  } = {},
): Promise<SupplyTableResponse | null> {
  const pageSize = options.pageSize ?? SUPPLY_TABLE_CHUNK_SIZE;

  // Con force_refresh no intentar dump primero: Redis puede estar caliente con
  // foto stale. page=1 + force_refresh hace bump+rebuild; luego reintentamos dump.
  if (!options.skipCatalogDump && !options.forceRefreshFirstPage) {
    const dumpHit = await tryCatalogDump(apiFilters, options.signal);
    if (options.isStale?.()) return null;
    if (dumpHit) {
      const totalCount = dumpHit.pagination?.total_count ?? dumpHit.data.length;
      options.onFirstPage?.(
        buildMergedSupplyResponse(dumpHit, dumpHit.data, totalCount, 1, Math.max(totalCount, 1)),
        1,
      );
      return buildMergedSupplyResponse(
        dumpHit,
        dumpHit.data,
        totalCount,
        1,
        Math.max(totalCount, 1),
      );
    }
  }

  const first: SupplyTableResponse = await supplyService.getSupplyTableData(
    {
      ...apiFilters,
      page: 1,
      page_size: pageSize,
      ...(options.forceRefreshFirstPage ? { force_refresh: true } : {}),
    },
    { signal: options.signal },
  );
  if (options.isStale?.()) return null;

  const totalCount = first.pagination?.total_count ?? first.data.length;
  const totalPages = Math.max(
    1,
    first.pagination?.total_pages ?? Math.ceil(totalCount / pageSize),
  );
  let allRows: SupplyTableProduct[] = [...first.data];
  options.onFirstPage?.(
    buildMergedSupplyResponse(first, allRows, totalCount, totalPages, pageSize),
    totalPages,
  );

  if (totalPages <= 1) {
    return buildMergedSupplyResponse(first, allRows, totalCount, totalPages, pageSize);
  }

  // Tras page=1 Redis suele estar caliente: un dump evita N×2000.
  if (!options.skipCatalogDump) {
    const dumpAfterWarm = await tryCatalogDump(apiFilters, options.signal);
    if (options.isStale?.()) return null;
    if (dumpAfterWarm) {
      const dumpCount = dumpAfterWarm.pagination?.total_count ?? dumpAfterWarm.data.length;
      return buildMergedSupplyResponse(
        first,
        dumpAfterWarm.data,
        dumpCount,
        1,
        Math.max(dumpCount, 1),
      );
    }
  }

  for (let page = 2; page <= totalPages; page += 1) {
    if (options.isStale?.() || options.signal?.aborted) return null;
    await yieldToMain();
    if (options.isStale?.() || options.signal?.aborted) return null;
    const pageRes = await supplyService.getSupplyTableData(
      {
        ...apiFilters,
        page,
        page_size: pageSize,
      },
      { signal: options.signal },
    );
    if (options.isStale?.()) return null;
    allRows = allRows.concat(pageRes.data);
  }

  if (options.isStale?.()) return null;
  return buildMergedSupplyResponse(first, allRows, totalCount, totalPages, pageSize);
}
